import { useState, useEffect, useCallback, useRef } from 'react';
import { tasksApi } from '../api';

const DEBOUNCE_MS = 280;

export function useSearch(allTasks, isOnline) {
  const [query,      setQuery]      = useState('');
  const [apiResults, setApiResults] = useState([]);
  const [searching,  setSearching]  = useState(false);
  const timerRef    = useRef(null);
  // Stable ref for allTasks so localResults callback never needs it as dep
  const allTasksRef = useRef(allTasks);
  useEffect(() => { allTasksRef.current = allTasks; });

  // Local search — instant, no API, reads from ref
  const localResults = useCallback((q) => {
    if (!q.trim()) return [];
    const lower = q.toLowerCase();
    return allTasksRef.current.filter(t =>
      t.title?.toLowerCase().includes(lower) ||
      t.notes?.toLowerCase().includes(lower) ||
      t.category?.name?.toLowerCase().includes(lower)
    );
  }, []); // no deps — always reads from ref

  // API search — debounced
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!query.trim() || query.trim().length < 2) {
      setApiResults([]);
      setSearching(false);
      return;
    }

    if (!isOnline) {
      setApiResults([]);
      return;
    }

    setSearching(true);

    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await tasksApi.search(query.trim());
        setApiResults(data.data || data);
      } catch {
        setApiResults([]);
      } finally {
        setSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
  }, [query, isOnline]); // only query + isOnline — not allTasks

  // Merge local + API, deduplicate by id
  const local   = localResults(query);
  const apiIds  = new Set(apiResults.map(t => String(t.id)));
  const results = query.trim()
    ? (isOnline ? [...apiResults, ...local.filter(t => !apiIds.has(String(t.id)))] : local)
    : [];

  const clear = useCallback(() => {
    setQuery('');
    setApiResults([]);
    setSearching(false);
  }, []);

  return { query, setQuery, results, isSearching: searching, isActive: query.trim().length > 0, clear };
}

// Highlight matching substring
export function highlightMatch(text, query) {
  if (!text || !query.trim()) return [{ text, highlight: false }];
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return [{ text, highlight: false }];
  return [
    { text: text.slice(0, idx),                  highlight: false },
    { text: text.slice(idx, idx + query.length),  highlight: true  },
    { text: text.slice(idx + query.length),       highlight: false },
  ];
}